/**
 * ChartArtifact — interactive chart rendered from a `cf-chart/v1` spec.
 *
 * Distinct from `<ArtifactView>`'s image branch: we fetch the JSON spec
 * (small, ~5–50 KB), then lazy-load Plotly only on the first chart in a
 * session. Bundle cost is paid once per session, in a chunk separate
 * from the initial page load.
 *
 * Failure modes are surfaced inline rather than thrown:
 *   • fetch / 404 → "Chart unavailable"
 *   • bad JSON   → "Chart spec invalid"
 *   • bad schema → "Chart format unsupported"
 *
 * Each leaves the user looking at the assistant's prose instead of a
 * blank box, with the artifact label intact.
 */
import { env } from "@/env";
import type { ToolArtifact } from "@models/chat";
import BrokenImageIcon from "@mui/icons-material/BrokenImageOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { Suspense, lazy, useEffect, useState } from "react";
import type { ChartSetup, ChartSpec } from "./chartSpec";

// Lazy-load Plotly + the renderer in a separate chunk. Sessions with no
// chart artifacts never fetch this; sessions with one or more chart
// artifacts pay the chunk download once, then it's cached.
const PlotlyChart = lazy(() => import("./PlotlyChart"));

interface ChartArtifactProps {
  artifact: ToolArtifact;
  variant?: "inline" | "panel";
  /** When true, suppress the one-line "Transient (.tran 1u 500u) ·
   *  sources … · probes …" caption rendered above the figure. The
   *  Workbench Simulation tab sets this because the surrounding
   *  surface already presents analysis + stimulus + probes in a
   *  dedicated, structured layout — the chart caption would just
   *  echo the same info in a less legible form. */
  hideSetupHeader?: boolean;
}

type FetchState =
  | { status: "loading" }
  | { status: "ok"; spec: ChartSpec }
  | { status: "error"; reason: string };

function resolveSrc(url: string): string {
  return url.startsWith("/") ? `${env.VITE_API_BASE_URL}${url}` : url;
}

function isChartSpec(value: unknown): value is ChartSpec {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  if (v.schema !== "cf-chart/v1") return false;
  if (v.layout !== "single" && v.layout !== "bode") return false;
  return true;
}

export function ChartArtifact({
  artifact,
  variant = "panel",
  hideSetupHeader = false,
}: ChartArtifactProps) {
  const [state, setState] = useState<FetchState>({ status: "loading" });
  const src = resolveSrc(artifact.url);

  useEffect(() => {
    // AbortController cancels the in-flight request when src changes mid-fetch
    // (e.g. fast-scroll through chart artifacts) instead of letting the body
    // download to completion only to discard it.
    const ac = new AbortController();
    (async () => {
      try {
        const resp = await fetch(src, {
          credentials: "include",
          signal: ac.signal,
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const json = await resp.json();
        if (!isChartSpec(json)) {
          setState({ status: "error", reason: "format unsupported" });
          return;
        }
        setState({ status: "ok", spec: json });
      } catch (err) {
        if ((err as { name?: string })?.name === "AbortError") return;
        setState({
          status: "error",
          reason:
            err instanceof Error && /JSON/i.test(err.message)
              ? "spec invalid"
              : "unavailable",
        });
      }
    })();
    return () => ac.abort();
  }, [src]);

  const labelEl = artifact.label ? (
    <Typography
      variant="caption"
      sx={{
        display: "block",
        color: "text.secondary",
        fontSize: "0.7rem",
        mb: 0.5,
      }}
    >
      {artifact.label}
    </Typography>
  ) : null;

  const wrapperSx = variant === "inline" ? { my: 1.25 } : {};

  if (state.status === "loading") {
    return (
      <Box sx={wrapperSx}>
        {labelEl}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ py: 2, px: 1.5, color: "text.secondary" }}
        >
          <CircularProgress size={14} />
          <Typography variant="caption">Loading chart…</Typography>
        </Stack>
      </Box>
    );
  }

  if (state.status === "error") {
    return (
      <Box
        sx={{
          border: "1px dashed",
          borderColor: "border.default",
          borderRadius: 1,
          p: 1.25,
          ...wrapperSx,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <BrokenImageIcon sx={{ fontSize: 18, color: "text.secondary" }} />
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontSize: "0.75rem" }}
          >
            {artifact.label
              ? `"${artifact.label}" — chart ${state.reason}`
              : `Chart ${state.reason}`}
          </Typography>
        </Stack>
      </Box>
    );
  }

  // Caption (italic, beneath the chart) — the agent's one-sentence
  // takeaway. Renders BENEATH the figure so the chart itself is the
  // first thing the eye lands on.
  const caption = state.spec.caption?.trim();
  const setup = state.spec.setup;

  return (
    <Box sx={wrapperSx}>
      {labelEl}
      {setup && !hideSetupHeader && <SimulationSetupHeader setup={setup} />}
      <Box
        sx={{
          // Plotly renders its own dark surface, but we wrap in a thin
          // container so the chart sits inside the chat surface
          // consistently with other artifacts. No extra border — the
          // chart's `paper_bgcolor` already matches `surface.raised`.
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <Suspense
          fallback={
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ py: 2, px: 1.5, color: "text.secondary" }}
            >
              <CircularProgress size={14} />
              <Typography variant="caption">Rendering chart…</Typography>
            </Stack>
          }
        >
          <PlotlyChart
            spec={state.spec}
            downloadStem={artifact.label || "circuit-forge-chart"}
          />
        </Suspense>
      </Box>
      {caption && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: "text.secondary",
            fontStyle: "italic",
            fontSize: "0.78rem",
            mt: 0.5,
          }}
        >
          {caption}
        </Typography>
      )}
      {setup?.netlist && <SimulationNetlistExpander netlist={setup.netlist} />}
    </Box>
  );
}

/**
 * One-line "what was simulated" caption rendered above the chart.
 * Combines the analysis kind, the raw directive, source-element names,
 * and probe nets so the user can interpret the plot without expanding
 * the netlist. Bullet separators (·) keep the line skim-readable.
 */
function SimulationSetupHeader({ setup }: { setup: ChartSetup }) {
  const parts: string[] = [];
  if (setup.analysis_kind) {
    parts.push(
      setup.analysis_directive
        ? `${setup.analysis_kind} (${setup.analysis_directive})`
        : setup.analysis_kind,
    );
  } else if (setup.analysis_directive) {
    parts.push(setup.analysis_directive);
  }
  if (setup.source_names.length > 0) {
    parts.push(`sources: ${setup.source_names.join(", ")}`);
  }
  if (setup.probes.length > 0) {
    parts.push(`probes: ${setup.probes.join(", ")}`);
  }
  if (parts.length === 0) return null;
  return (
    <Typography
      variant="caption"
      sx={{
        display: "block",
        color: "text.secondary",
        fontSize: "0.72rem",
        mb: 0.5,
        fontFamily:
          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      }}
    >
      {parts.join(" · ")}
    </Typography>
  );
}

/**
 * Collapsible "show netlist" expander beneath the chart. Lets a user
 * inspect the exact `.cir` text that produced the plot — covers the
 * long tail of param sweeps, ``.ic``/``.nodeset`` directives, and
 * model lines that the one-line caption can't fit.
 */
function SimulationNetlistExpander({ netlist }: { netlist: string }) {
  return (
    <Accordion
      disableGutters
      square
      sx={{
        mt: 0.75,
        background: "transparent",
        boxShadow: "none",
        "&:before": { display: "none" },
        border: "1px solid",
        borderColor: "border.default",
        borderRadius: 1,
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
        sx={{
          minHeight: 0,
          px: 1.25,
          py: 0.25,
          "& .MuiAccordionSummary-content": { my: 0.5 },
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontSize: "0.72rem" }}
        >
          Show netlist
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.25, pt: 0, pb: 1 }}>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1,
            fontSize: "0.7rem",
            fontFamily:
              "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            whiteSpace: "pre",
            overflowX: "auto",
            color: "text.primary",
            background: "action.hover",
            borderRadius: 0.5,
          }}
        >
          {netlist}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
